import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthLoginResponseSuccessDto } from '../_generated';
import { AuthActions } from '../store/actionstypes/auth/auth.actiontype';
import { AuthState, authFeature } from '../store/reducers/auth.reducer';
import { getUserParameterTypes } from '../store/selectors/auth.selector';
import { ChangePasswordComponent } from './components/change-password.component';
import { ConfirmChangePasswordComponent } from './components/confirm-change-password.component';

@Component({
  standalone: true,
  imports: [TranslatePipe, RouterModule, FormsModule, ChangePasswordComponent, ConfirmChangePasswordComponent, NgOptimizedImage],
  template: `
    <div class="min-h-screen flex flex-col md:flex-row  h-screen md:h-auto bg-shades-white">
      <!-- Left Panel: Welcome Message -->
      <div class="w-full md:w-1/2 flex items-center justify-center p-12 ">
        <div class="max-w-md">
          <img ngSrc="/assets/logo.svg" alt="Logo" width="50" height="20" />
          <h3 class="typo-h3">
            {{ 'login.welcome_title' | translate }}
          </h3>
          <p class="typo-p2">
            {{ 'login.welcome_message' | translate }}
          </p>
        </div>
      </div>

      <!-- Right Panel: Login Form or Change Password Form -->
      <div class="w-full md:w-1/2 flex items-center justify-center p-12 bg-shades-white md:bg-neutral-50">
        <div class="w-full max-w-xs space-y-8">
          @if (!isChangePasswordRequired() && !isConfirmChangePasswordRequired()) {
            <router-outlet class="mt-8 space-y-6"></router-outlet>
          }

          @if (isChangePasswordRequired()) {
            <app-change-password class="mt-8 space-y-6"></app-change-password>
          }

          @if (isConfirmChangePasswordRequired()) {
            <app-confirm-change-password class="mt-8 space-y-6"></app-confirm-change-password>
          }
        </div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginPage {
  private readonly router = inject(Router);
  private readonly authStore = inject<Store<AuthState>>(Store);

  private readonly userParameterTypes = this.authStore.selectSignal(getUserParameterTypes);
  readonly isChangePasswordRequired = this.authStore.selectSignal(authFeature.selectIsChangePasswordRequired);
  readonly isConfirmChangePasswordRequired = this.authStore.selectSignal(authFeature.selectIsConfirmChangePasswordRequired);

  email = '';
  password = '';
  errorMessage = '';
  session: AuthLoginResponseSuccessDto | null = null;

  constructor() {
    effect(() => {
      const userParameterTypes = this.userParameterTypes();
      if (userParameterTypes.length > 0) {
        // Navigate to the correct page based on the user's role first parameter type
        void this.router.navigate([`/${userParameterTypes[0].replace('_', '-')}`]);
      }
    });
  }

  async onSubmit() {
    this.errorMessage = '';

    if (!this.validateLoginForm()) {
      return;
    }

    try {
      this.authStore.dispatch(AuthActions.signIn({ email: this.email, password: this.password }));
    } catch (error) {
      console.error('Login failed:', error);
      this.errorMessage = 'login.invalid_credentials';
    }
  }

  validateLoginForm(): boolean {
    if (!this.email || !this.password) {
      this.errorMessage = 'login.enter_both_fields';
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)) {
      this.errorMessage = 'login.invalid_email';
      return false;
    }
    return true;
  }
}
